/* What a spot can be.
   A spot is a story about who acted before whom, so nearly every rule falls out of one
   fact: the order the seats act in preflop. Seats are listed in that order, and an action
   says how many opponents it names and on which side of hero they sit. Everything else
   derives. */

import { seatName } from "./seats";
import { ROLE_OF } from "./spot";

/** The preflop actions a chart can answer, in the order the picker lists them. */
export const ACTIONS = [
  "RFI",
  "vs. RFI",
  "vs. 3 Bet",
  "vs. 4 Bet",
  "vs. 5 Bet",
  "Cold 4bet",
  "vs. Raise-Call",
  "vs. Squeeze",
  "Called vs. Squeeze",
] as const;

/* Actions are stored written out, so the data reads as English, and shown short, so the
   picker is a row of buttons rather than a paragraph. An action the map has not been told
   about is shown as it is stored. */
export const ACTION_LABEL: Readonly<Record<string, string>> = {
  RFI: "RFI",
  "vs. RFI": "vRFI",
  "vs. 3 Bet": "v3B",
  "vs. 4 Bet": "v4B",
  "vs. 5 Bet": "v5B",
  "Cold 4bet": "c4B",
  "vs. Raise-Call": "vR&C",
  "vs. Squeeze": "vSQ",
  "Called vs. Squeeze": "CvSQ",
};
export const actionLabel = (a: string): string => ACTION_LABEL[a] ?? a;

/* The short labels are for the buttons; these are what they stand for, shown on hover.
   Written as the table sees it: who has put money in before hero. */
export const ACTION_HELP: Readonly<Record<string, string>> = {
  RFI: "Raise first in — the pot is unopened",
  "vs. RFI": "Facing an open raise",
  "vs. 3 Bet": "Hero opened and was 3-bet",
  "vs. 4 Bet": "Hero 3-bet and was 4-bet",
  "vs. 5 Bet": "Hero 4-bet and was jammed on",
  "Cold 4bet": "An open and a 3-bet in front, hero yet to act",
  "vs. Raise-Call": "An open and a caller in front",
  "vs. Squeeze": "Hero opened, a seat called, a seat behind raised",
  "Called vs. Squeeze": "Hero called an open, then a seat behind squeezed",
};
export const actionHelp = (a: string): string => ACTION_HELP[a] ?? a;

/* Seat names as a live table says them. */
export const SEAT_HELP: Readonly<Record<string, string>> = {
  LJ: "Lojack — first to act",
  HJ: "Hijack",
  CO: "Cutoff",
  BTN: "Button",
  SB: "Small blind",
  BB: "Big blind",
};
export const seatHelp = (v: string): string => SEAT_HELP[v] ?? v;

/* What the seat that opened did about the raise behind it. */
export const BRANCH_HELP: Readonly<Record<string, string>> = {
  Called: "It called the raise",
  Folded: "It folded and left its bet behind",
  "4-Bet": "It came back over the raise for a size, short of all-in",
  Jammed: "It moved all-in over the raise",
};

export const SEAT_ORDER = {
  hu: ["SB", "BB"], // SB is the button and acts first
  ring: ["LJ", "HJ", "CO", "BTN", "SB", "BB"],
} as const;
export const isHU = (gameType: string): boolean => gameType === "HU Cash";
export const seatsFor = (gameType: string): readonly string[] =>
  SEAT_ORDER[isHU(gameType) ? "hu" : "ring"];

type Side = "before" | "after";
interface Rule {
  opps: number;
  side?: Side;
  sides?: readonly Side[];
  heroNotLast?: boolean;
}

// side: where the named opponents sit relative to hero. Two "before" seats are the raiser
// and the caller, in that order, so they are strictly ascending.
export const RULES: Readonly<Record<string, Rule>> = {
  RFI: { opps: 0, heroNotLast: true },
  "vs. RFI": { opps: 1, side: "before" },
  "vs. 3 Bet": { opps: 1, side: "after" },
  "vs. 4 Bet": { opps: 1, side: "before" },
  "vs. 5 Bet": { opps: 1, side: "after" },
  "Cold 4bet": { opps: 2, side: "before" },
  "vs. Raise-Call": { opps: 2, side: "before" },
  "vs. Squeeze": { opps: 2, side: "after" },
  // Hero flatted someone else's open, so the seats it names straddle it: the raiser is
  // behind, the squeezer in front. The only action with two sides.
  "Called vs. Squeeze": { opps: 2, sides: ["before", "after"] },
};
/** Which side of hero opponent slot `i` sits on. */
const sideAt = (rule: Rule, i: number): Side | undefined =>
  rule.sides ? rule.sides[i] : rule.side;

/* Hero is not always the only seat still to act after the last raise. Where an opponent
   answers it before hero does, that answer is part of which chart this is: hero plays a
   squeeze the opener called nothing like one it folded to. The answers are listed in the
   order the source files write them, the re-raise before the jam it stops short of. */
export const BRANCHES: Readonly<Record<string, readonly string[]>> = {
  "Called vs. Squeeze": ["Called", "Folded", "4-Bet", "Jammed"],
};
export const branchesFor = (action: string): readonly string[] | null => BRANCHES[action] ?? null;
/** Whose answer it is: the seat that opened, which is the first one named. */
export const branchLabel = (seats: readonly string[]): string =>
  `${seatName(seats[0] ?? "") || "Raiser"}'s Answer`;

const ANSWER_TO_RAISE: Readonly<Record<string, string>> = {
  "Cold 4bet": "3-bet",
  "vs. Raise-Call": "call",
};

/* Two seats that both act after hero are in the order they spoke: the first answers hero's
   raise, the second answers that answer. */
const ANSWER_TO_ANSWER: Readonly<Record<string, string>> = { "vs. Squeeze": "squeeze over" };

/* What the opponent did to reach this spot, as a clause. The short names cannot be read as
   one, and the written-out ones only ever could by accident. */
const FACED: Readonly<Record<string, string>> = {
  "vs. 3 Bet": "3-bet hero's open",
  "vs. 4 Bet": "4-bet hero's 3-bet",
  "vs. 5 Bet": "jammed over hero's 4-bet",
  "vs. Squeeze": "answered hero's open",
};

export interface PriorStep {
  prior: string;
  via: readonly ("allin" | "raise" | "call")[];
  did: string;
  does: string;
}

/* Where hero has already acted: which chart holds that earlier decision, and which action
   inside it actually leads here. Only a raise does. Facing a re-raise means hero put in a
   raise and still has chips and a decision left, so a call never carries forward — a caller
   is not the one being 4-bet — and neither does a shove, because nobody re-raises a hand
   that is already all-in. `did` names it in the past, `does` in the present. */
export const HERO_ALREADY_ACTED: Readonly<Record<string, PriorStep>> = {
  "vs. 3 Bet": { prior: "RFI", via: ["raise"], did: "opened with", does: "opens" },
  "vs. 4 Bet": { prior: "vs. RFI", via: ["raise"], did: "3-bet", does: "3-bets" },
  "vs. 5 Bet": { prior: "vs. 3 Bet", via: ["raise"], did: "4-bet", does: "4-bets" },
  "vs. Squeeze": { prior: "RFI", via: ["raise"], did: "opened with", does: "opens" },
  // The one place a call carries forward: hero flatted and is still to act, because the
  // squeeze came in behind it.
  "Called vs. Squeeze": { prior: "vs. RFI", via: ["call"], did: "called", does: "calls" },
};

export const oppCount = (action: string): number => RULES[action]?.opps ?? 0;

const tableName = (gameType: string): string => (isHU(gameType) ? "heads-up" : "6-max");

/** Why this combination cannot happen at a real table, or "" if it can. */
export function spotFault(
  gameType: string,
  hero: string,
  action: string,
  seats: readonly (string | null)[] = [],
): string {
  const rule = RULES[action];
  if (!rule) return `"${actionLabel(action)}" is not a preflop action this app knows.`;

  const order = seatsFor(gameType);
  const at = (s: string | null): number => (s === null ? -1 : order.indexOf(s));
  const table = tableName(gameType);
  // The rules read ids; the sentences say what the user calls the seats.
  const n = (s: string | null): string => (s === null ? String(s) : seatName(s));

  if (at(hero) < 0) return `${n(hero)} does not sit at a ${table} table.`;
  if (seats.length !== rule.opps)
    return `${actionLabel(action)} names ${rule.opps} opponent${rule.opps === 1 ? "" : "s"}, not ${seats.length}.`;

  // Folded to the big blind is not a spot: there is nobody left to raise into.
  if (rule.heroNotLast && at(hero) === order.length - 1)
    return `${n(hero)} cannot raise first in — everyone has already folded to it.`;

  for (let i = 0; i < seats.length; i++) {
    const o = seats[i] ?? null;
    const role = ROLE_OF[action]?.[i] ?? `Opponent ${i + 1}`;
    if (at(o) < 0) return `${n(o)} does not sit at a ${table} table.`;
    if (o === hero) return `${role.toLowerCase()} cannot be ${n(hero)} — that is hero.`;
    if (seats.indexOf(o) !== i) return `${n(o)} is named twice.`;
    const side = sideAt(rule, i);
    if (side === "before" && at(o) > at(hero))
      return `${n(o)} acts after ${n(hero)}, so it cannot be the ${role.toLowerCase()} here.`;
    if (side === "after" && at(o) < at(hero))
      return `${n(o)} acts before ${n(hero)}, so it cannot have ${FACED[action] ?? "answered hero's open"}.`;
  }
  const [first, second] = [seats[0] ?? null, seats[1] ?? null];
  // Raiser opens, the second opponent answers it: that answer can only come from a seat
  // between the raiser and hero.
  if (rule.side === "before" && seats.length === 2 && at(first) >= at(second))
    return `${n(second)} acts before ${n(first)}, so it cannot ${ANSWER_TO_RAISE[action] ?? "answer"} its raise.`;
  // Both behind hero: the first answered hero's raise, the second answered that, so they
  // are in the order they spoke.
  if (rule.side === "after" && seats.length === 2 && at(first) >= at(second))
    return `${n(second)} acts before ${n(first)}, so it cannot ${ANSWER_TO_ANSWER[action] ?? "answer"} it.`;

  return "";
}

export const spotIsLegal = (
  gameType: string,
  hero: string,
  action: string,
  seats?: readonly (string | null)[],
): boolean => spotFault(gameType, hero, action, seats) === "";

/** Nearest legal opponent set, keeping what still holds. null if none exists. */
export function autoSeats(
  gameType: string,
  hero: string,
  action: string,
  seats: readonly (string | null)[] = [],
): string[] | null {
  const n = oppCount(action);
  const pool = seatsFor(gameType);
  const want: (string | null)[] = seats.slice(0, n);
  while (want.length < n) want.push(null);

  // Backtracking fill. Each slot prefers the seat already there, so changing the action or
  // hero disturbs the draft as little as it can.
  const out: (string | null)[] = new Array<string | null>(n).fill(null);
  const fill = (i: number): boolean => {
    if (i === n) return spotIsLegal(gameType, hero, action, out);
    const kept = want[i] ?? null;
    const tries = kept && pool.includes(kept) ? [kept, ...pool.filter((v) => v !== kept)] : pool;
    for (const v of tries) {
      if (v === hero || out.slice(0, i).includes(v)) continue;
      out[i] = v;
      if (fill(i + 1)) return true;
    }
    out[i] = null;
    return false;
  };
  return fill(0) ? (out as string[]) : null;
}

/** Seats hero could occupy for this action — those with some legal opponent set. */
export const legalHeroes = (gameType: string, action: string): string[] =>
  seatsFor(gameType).filter((h) => autoSeats(gameType, h, action, []) !== null);

/** Actions this hero could be facing. */
export const legalActions = (gameType: string, hero: string): string[] =>
  ACTIONS.filter((a) => autoSeats(gameType, hero, a, []) !== null);

/** Values opponent slot i may take, holding the other slots as they are. */
export function legalSeatAt(
  gameType: string,
  hero: string,
  action: string,
  seats: readonly (string | null)[],
  i: number,
): string[] {
  return seatsFor(gameType).filter((v) => {
    const trial = [...seats];
    trial[i] = v;
    return spotIsLegal(gameType, hero, action, trial);
  });
}

/* Tooltips for the pickers. A blocked button has to say why, or it reads as a bug rather
   than a rule. */
export function heroFault(gameType: string, action: string | null, hero: string | null): string {
  if (!action || !hero) return "";
  const order = seatsFor(gameType);
  const rule = RULES[action];
  if (order.indexOf(hero) < 0)
    return `${seatName(hero)} does not sit at a ${tableName(gameType)} table.`;
  if (!rule || autoSeats(gameType, hero, action, [])) return "";
  if (rule.heroNotLast && order.indexOf(hero) === order.length - 1)
    return `${seatName(hero)} cannot raise first in — everyone has already folded to it.`;
  const before = order.indexOf(hero);
  const after = order.length - 1 - before;
  const room = (side: string): number => (side === "after" ? after : before);
  // A two-sided action fails on whichever side is short, and names that side.
  const sides = rule.sides ?? new Array<Side | undefined>(rule.opps).fill(rule.side);
  const need: Record<string, number> = {};
  for (const sd of sides) if (sd) need[sd] = (need[sd] ?? 0) + 1;
  for (const [sd, n] of Object.entries(need)) {
    const have = room(sd);
    if (have >= n) continue;
    return (
      `${actionLabel(action)} needs ${n} seat${n === 1 ? "" : "s"} acting ${sd} ${seatName(hero)}, and there ` +
      (have === 0 ? "are none." : `${have === 1 ? "is" : "are"} only ${have}.`)
    );
  }
  return `${seatName(hero)} cannot be in a ${actionLabel(action)} spot at this table.`;
}

/* Where hero sits comes first, and the action is read against it: LJ has nobody acting
   before it, so it can never be facing an RFI, and that button is dead rather than quietly
   reseating hero. The seat is the free dimension for the same reason blocking both would
   deadlock the draft — heads-up ties each action to one seat, so a strict pair could never
   be crossed. Clicking a seat therefore moves the action to the nearest one that seat can
   be in, and says so. Every legal pair stays two clicks away: pick the seat, then the
   action, which the seat has just made reachable. */
export function nearestHero(gameType: string, action: string, was: string): string | null {
  const pool = seatsFor(gameType);
  const at = pool.indexOf(was);
  return (
    legalHeroes(gameType, action).sort(
      (a, b) => Math.abs(pool.indexOf(a) - at) - Math.abs(pool.indexOf(b) - at),
    )[0] ?? null
  );
}

/** Nearest action this hero can be in, counting from the one asked for. */
export function nearestAction(gameType: string, hero: string, was: string): string | null {
  const pool: readonly string[] = ACTIONS;
  const at = pool.indexOf(was);
  return (
    legalActions(gameType, hero).sort(
      (a, b) => Math.abs(pool.indexOf(a) - at) - Math.abs(pool.indexOf(b) - at),
    )[0] ?? null
  );
}

/** The part of a creator draft the rules read and move. */
export interface SeatDraft {
  gameType: string;
  hero: string;
  action: string;
  seats: (string | null)[];
  /** What the last `adopt` had to move to fit, as "was → now" lines. */
  moved?: string[];
}

/** Snap the draft's opponents to something the action allows, keeping what still holds.
    Leaves seats empty when the combination has no legal set at all, so the picker can say
    why instead of inventing one. */
export function fitSeats(c: SeatDraft): void {
  c.seats =
    autoSeats(c.gameType, c.hero, c.action, c.seats) ??
    new Array<string | null>(oppCount(c.action)).fill(null);
}

/** Take one choice as given and move whatever else has to move to fit it.
    Records what it moved, so the panel can say so rather than silently reseat. */
export function adopt<D extends SeatDraft, K extends keyof D & string>(
  c: D,
  dim: K,
  v: D[K],
): void {
  const was = { hero: c.hero, action: c.action };
  c[dim] = v;
  if (dim === "hero" && !legalActions(c.gameType, c.hero).includes(c.action))
    c.action = nearestAction(c.gameType, c.hero, was.action) ?? c.action;
  // Kept for the moves nobody clicked: switching game type can strand a hero on a seat the
  // new table does not have.
  if (dim !== "hero" && !legalHeroes(c.gameType, c.action).includes(c.hero))
    c.hero = nearestHero(c.gameType, c.action, was.hero) ?? c.hero;
  fitSeats(c);
  const named = (k: "hero" | "action", val: string): string =>
    k === "action" ? actionLabel(val) : seatName(val);
  c.moved = (["hero", "action"] as const)
    .filter((k) => k !== dim && c[k] !== was[k])
    .map((k) => `${named(k, was[k])} → ${named(k, c[k])}`);
}

/** Why no spot at this table can put hero on this seat, or "" when one can. */
export function seatUnusable(gameType: string, hero: string): string {
  const table = tableName(gameType);
  if (!seatsFor(gameType).includes(hero))
    return `${seatName(hero)} does not sit at a ${table} table.`;
  return legalActions(gameType, hero).length
    ? ""
    : `No preflop spot at a ${table} table puts hero on ${seatName(hero)}.`;
}

/** Why hero cannot be facing this action, or "" when it can. The action panel reads the
    same rule the hero panel does, from the other end. */
export function actionFault(gameType: string, hero: string | null, action: string): string {
  if (!legalHeroes(gameType, action).length)
    return `No seat at a ${tableName(gameType)} table can be in a ${actionLabel(action)} spot.`;
  return heroFault(gameType, action, hero);
}

/** Why opponent slot i cannot be v, holding the rest of the draft as it is. */
export function seatFault(
  gameType: string,
  hero: string,
  action: string,
  seats: readonly (string | null)[],
  i: number,
  v: string,
): string {
  const trial = [...seats];
  trial[i] = v;
  return spotFault(gameType, hero, action, trial);
}
